/**
 * tokens/semantic.ts — Semantic color tokens
 *
 * AssetWrx Design System v2 · Direction A: Refined Operator
 *
 * THIS IS THE LAYER THE PRODUCT CONSUMES.
 *
 * Semantic tokens map ROLES to palette values. Components ONLY read
 * from this file. To re-skin the app, change only this file.
 *
 * Naming: <category>.<role>.<variant?>
 *   surface.* — backgrounds
 *   text.*    — foreground type
 *   border.*  — strokes & dividers
 *   icon.*    — icon fills
 *   focus.*   — focus rings
 *   code.*    — inline code / asset ID chips
 */

import { palette } from '../colors';

// ---------------------------------------------------------------------------
// LIGHT MODE
// ---------------------------------------------------------------------------
export const lightSemantic = {
  surface: {
    // Canvas sits a clear step below white so raised cards visibly lift off the
    // page (the old neutral[50] #F7F8F9 was only ~3% off white, reading as one
    // flat plane). It's ~8% below white — a confident premium canvas depth —
    // with `sunken` pushed a matching step deeper so the ladder
    // white > canvas > sunken still reads (dividers/pills stay below the canvas).
    //
    // COOL PAPER (owner direction, Aug 2026 — "Ink & Amber"): the Jul 2026 warm
    // canvas (#EDEBE7) was reverted. Against ink chrome and white cards the beige
    // read muddy, and it muted the amber accent it was meant to warm. #F4F5F7 is
    // a cool paper a clear step below white, so raised cards visibly lift and the
    // amber spark stays the only warm note on the screen. This deliberately
    // reverses the earlier warm direction — do not "fix" it back to #EDEBE7.
    canvas:        '#F4F5F7',                      // app background — cool paper
    raised:        palette.neutral[0],             // cards, list rows, inputs
    sunken:        '#E9EBEF',                      // section dividers, code blocks
    inverse:       palette.neutral[900],           // dark toolbars, nav chrome
    overlay:       'rgba(20, 25, 37, 0.60)',       // modal scrims
    // Selected rows return to the amber wash. The warm sand (#EADFCB) existed
    // only because the canvas had gone beige; on cool paper that tan reads as a
    // stain, while signal[50] reads as a deliberate highlight again — which is
    // why it was the original choice. Selection is an "active" state, so amber
    // carrying it is consistent with amber meaning live/in-flight elsewhere. The
    // brand-ink `selectedBorder` still carries the definition.
    selected:      palette.signal[50],             // selected list row — amber wash
    hover:         palette.neutral[100],
    pressed:       palette.neutral[200],
    disabled:      palette.neutral[100],

    // Chrome — nav rail, tab bar and toolbars. Ink in BOTH modes: the dark shell
    // against light content is the product's signature, and neither competitor
    // has one. Deliberately not `inverse`, which flips with the theme.
    chrome:        palette.neutral[900],
    chromeSunken:  palette.neutral[950],           // recessed wells inside chrome

    brand: {
      subtle:      palette.brand[50],
      muted:       palette.brand[100],
      // PRIMARY CTA is the brand INK, not the middle of the slate ramp.
      // brand[500] (#52616B) was a washed-out mid-grey that read as *disabled*
      // on every primary button in the app. Ink is 18:1 on white and decisive.
      // Hover/press step UP the ramp (ink is already near-black).
      solid:       palette.brand[900],             // primary CTA — the ink itself
      solidHover:  palette.brand[800],
      solidPress:  palette.brand[700],
    },

    status: {
      // Success takes its 600 for the same reason warning does (below): the
      // solid is read as TEXT across the product, and the 500 measured 4.46:1
      // on a sunken chip and 4.39:1 on its own soft tint — both just under AA.
      // Danger's 500 clears at 5.25:1 and stays put.
      success: { subtle: palette.success[50],  muted: palette.success[100], solid: palette.success[600] },
      // Warning's solid is the 600, not the 500 like its siblings.
      //
      // `p.warning` resolves here, and dozens of surfaces use it as a TEXT
      // colour as well as a fill. At the 500 (#B45D0E) that measured 4.27:1 on
      // the canvas and 3.90:1 on a sunken chip — under AA in both. Success
      // (4.88) and danger (5.74) clear it at their 500s; warning is the one
      // hue whose 500 is too light to be read, because the ramp is
      // orange-leaning and orange peaks bright.
      //
      // Darkening the solid serves both jobs: white-on-fill only improves, and
      // every `color: p.warning` site is lifted at once instead of being
      // chased individually.
      warning: { subtle: palette.warning[50],  muted: palette.warning[100], solid: palette.warning[600] },
      danger:  { subtle: palette.danger[50],   solid: palette.danger[500]  },
      info:    { subtle: palette.info[50],     solid: palette.info[500]    },
    },
  },

  text: {
    primary:       palette.neutral[900],
    secondary:     palette.neutral[700],
    tertiary:      palette.neutral[500],
    disabled:      palette.neutral[400],
    // `inverse`  — flips with the theme. Use for text on the theme's INVERSE
    //              surface (e.g. light text on a dark toolbar in light mode).
    // `onBrand`  — text rendered on `surface.brand.solid`. Mode-aware.
    // `onStatus` — text rendered on `surface.status.*.solid`. Status colours
    //              stay saturated in both modes, so this is ALWAYS WHITE.
    //              For solid-colour danger / success / warning buttons, use
    //              this (or `colors.white`) — never `inverse`, which would
    //              read dark in dark mode.
    inverse:       palette.neutral[0],
    onBrand:       palette.neutral[0],
    onStatus:      palette.neutral[0],
    brand:         palette.brand[600],
    link:          palette.brand[600],
    linkHover:     palette.brand[700],

    status: {
      success: palette.success[600],
      warning: palette.warning[700],               // darker step for contrast
      danger:  palette.danger[600],
      info:    palette.info[600],
    },
  },

  border: {
    subtle:        palette.neutral[200],            // card edges, list dividers
    // input / control borders — must clear WCAG 1.4.11 non-text contrast (3:1)
    // against the input fill. neutral[300] was 1.86:1 on white (fail); [400] is
    // 3.15:1 (pass). Card/divider edges keep the lighter `subtle`.
    default:       palette.neutral[400],            // input borders (3:1)
    strong:        palette.neutral[500],            // stays above `default`
    inverse:       palette.neutral[700],
    brand:         palette.brand[500],
    disabled:      palette.neutral[200],

    status: {
      success: palette.success[500],
      warning: palette.warning[500],
      danger:  palette.danger[500],
      info:    palette.info[500],
    },
  },

  icon: {
    primary:       palette.neutral[700],
    secondary:     palette.neutral[500],
    tertiary:      palette.neutral[400],
    inverse:       palette.neutral[0],
    brand:         palette.brand[500],
    onBrand:       palette.neutral[0],
    onStatus:      palette.neutral[0],   // icon on solid status fill — always white

    status: {
      success: palette.success[600],
      warning: palette.warning[600],
      danger:  palette.danger[600],
      info:    palette.info[600],
    },
  },

  focus: {
    // Signal amber for focus — high-contrast on any surface and reinforces
    // the brand's "this is the live element" thesis (same colour as the
    // logo dot). Brand ink as a focus ring is invisible on dark surfaces;
    // signal works in both modes. signal[500] was 2.44:1 on white (fails the
    // 3:1 non-text minimum); signal[600] is 3.69:1 and still reads as amber.
    ring:          palette.signal[600],
    ringOffset:    palette.neutral[0],
  },

  code: {
    bg:            palette.accent[50],
    text:          palette.accent[700],
    border:        palette.accent[100],
  },
} as const;

// ---------------------------------------------------------------------------
// DARK MODE — same hue, stepped up the ramp for legibility on dark canvas.
// ---------------------------------------------------------------------------
export const darkSemantic = {
  surface: {
    // Cards must read as cards on the canvas: raised on 900 was 1.06:1 against
    // the 950 canvas, so sections vanished in dark mode. Cards step to 800 and
    // fields/chips (sunken) one above that.
    canvas:        palette.neutral[950],
    raised:        palette.neutral[800],
    sunken:        palette.neutral[750],
    inverse:       palette.neutral[0],
    overlay:       'rgba(0, 0, 0, 0.72)',
    selected:      palette.brand[900],
    hover:         palette.neutral[700],
    pressed:       palette.neutral[600],
    disabled:      palette.neutral[750],

    // Chrome stays ink in dark too, lifted one step so it separates from the
    // darker canvas rather than merging into it.
    chrome:        palette.neutral[900],
    chromeSunken:  palette.neutral[950],

    brand: {
      subtle:      palette.brand[900],
      muted:       palette.brand[800],
      // Dark mode's primary was palette.brand[400] (#8A929B) — the same
      // washed-out mid-grey that made every light-mode CTA read as disabled,
      // just one step up the ramp. Light mode answered this with ink; on a dark
      // canvas the equivalent decisive move is the inverse: a near-white fill
      // carrying ink text (`text.onBrand` is already neutral[950] here), which
      // is 18:1 and unmistakably the primary action.
      //
      // Hover/press step toward white and then back down, so the direction of
      // travel still reads as "lift" on a dark ground.
      //
      // `primary` also supplies the border on Button's outline/secondary
      // variants and Card's focus ring. A near-white edge is the correct idiom
      // on a dark surface, so those inherit sensibly rather than needing
      // separate treatment.
      solid:       palette.brand[100],
      solidHover:  palette.brand[50],
      solidPress:  palette.brand[200],
    },

    status: {
      success: { subtle: palette.success[900], muted: palette.success[800], solid: palette.success[400] },
      warning: { subtle: palette.warning[900], muted: palette.warning[800], solid: palette.warning[400] },
      danger:  { subtle: palette.danger[900],  solid: palette.danger[400]  },
      info:    { subtle: palette.info[700],    solid: palette.info[500]    },
    },
  },

  text: {
    primary:       palette.neutral[50],
    secondary:     palette.neutral[200],
    tertiary:      palette.neutral[400],
    disabled:      palette.neutral[600],
    // `inverse` flips with the theme; on dark scheme it's the brand ink so
    // text reads correctly on a light inverse surface. `onStatus` is ALWAYS
    // white because saturated status fills (danger / success / warning) keep
    // the same hue in both modes — use this (or `colors.white`) for text on
    // solid-colour buttons, never `inverse`.
    inverse:       palette.neutral[900],
    onBrand:       palette.neutral[950],
    onStatus:      palette.neutral[0],
    brand:         palette.brand[300],
    link:          palette.brand[300],
    linkHover:     palette.brand[200],

    status: {
      success: palette.success[300],
      warning: palette.warning[300],
      danger:  palette.danger[300],
      info:    palette.info[500],
    },
  },

  border: {
    subtle:        palette.neutral[700],   // card / divider edges, visible on the 800 card
    // Input / control borders need 3:1 on the dark input fill. neutral[700] was
    // 1.39:1 on raised (fail); neutral[400] is 5.68:1 (pass). Card/divider edges
    // keep the darker `subtle`.
    default:       palette.neutral[400],
    strong:        palette.neutral[300],            // stays above `default` (lighter = more prominent on dark)
    inverse:       palette.neutral[200],
    brand:         palette.brand[400],
    disabled:      palette.neutral[800],

    status: {
      success: palette.success[400],
      warning: palette.warning[400],
      danger:  palette.danger[400],
      info:    palette.info[500],
    },
  },

  icon: {
    primary:       palette.neutral[100],
    secondary:     palette.neutral[300],
    tertiary:      palette.neutral[500],
    inverse:       palette.neutral[900],
    brand:         palette.brand[300],
    onBrand:       palette.neutral[950],
    onStatus:      palette.neutral[0],   // icon on solid status fill — always white

    status: {
      success: palette.success[300],
      warning: palette.warning[300],
      danger:  palette.danger[300],
      info:    palette.info[500],
    },
  },

  focus: {
    // Signal amber stays visible on a dark canvas. See the light-mode
    // comment above — focus is the brand spark in both modes.
    ring:          palette.signal[400],
    ringOffset:    palette.neutral[950],
  },

  code: {
    bg:            palette.accent[700],
    text:          palette.accent[50],
    border:        palette.accent[600],
  },
} as const;

export type SemanticTokens = typeof lightSemantic | typeof darkSemantic;

export const semantic = { light: lightSemantic, dark: darkSemantic };
